/**
 * AgentContext - State management for AI dashboard generation
 *
 * Manages:
 * - Chat message history (session-only)
 * - Generated dashboard configuration
 * - Loading and error states
 * - Available metrics
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { generateDashboard, getAvailableMetrics, applyConfiguration } from '../services/agentService';

const AgentContext = createContext(null);

export const useAgent = () => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgent must be used within an AgentProvider');
  }
  return context;
};

export const AgentProvider = ({ children }) => {
  // Chat state
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Generation state
  const [generatedDashboard, setGeneratedDashboard] = useState(null);
  const [yamlConfig, setYamlConfig] = useState(null);
  const [validationResult, setValidationResult] = useState(null);

  // Metrics state
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(false);

  /**
   * Fetch available metrics from backend
   */
  const fetchMetrics = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const response = await getAvailableMetrics();
      if (response.success) {
        setAvailableMetrics(response.metrics);
      }
    } catch (err) {
      console.error('Failed to fetch metrics:', err);
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  /**
   * Generate dashboard from user objective with multi-turn conversation support
   */
  const generate = useCallback(async (objective, period = 'Monthly', templatePreference = 'four_columns_three_rows', dashboardName = null, startsFrom = '2024-07-01') => {
    setLoading(true);
    setError(null);

    // Add user message to chat
    const userMessage = {
      role: 'user',
      content: objective,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      // Build conversation history from current messages (excluding the current user message)
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await generateDashboard({
        objective,
        period,
        template_preference: templatePreference,
        dashboard_name: dashboardName,
        starts_from: startsFrom,
        conversation_history: conversationHistory,
        current_dashboard_yaml: yamlConfig  // Include current dashboard for iterative refinement
      });

      if (response.success) {
        const mode = response.mode || 'generate';

        // Handle different response modes
        if (mode === 'question' || mode === 'recommend') {
          // Agent is asking questions or providing recommendations
          const assistantMessage = {
            role: 'assistant',
            content: response.reasoning,
            timestamp: new Date().toISOString(),
            mode: mode
          };
          setMessages(prev => [...prev, assistantMessage]);

          // Clear any previous generation (since agent needs more info)
          setGeneratedDashboard(null);
          setYamlConfig(null);
          setValidationResult(null);

        } else if (mode === 'generate') {
          // Agent generated a dashboard
          setGeneratedDashboard(response.generated_dashboard);
          setYamlConfig(response.yaml_config);
          setValidationResult(response.validation);

          // Add assistant message to chat
          const assistantMessage = {
            role: 'assistant',
            content: response.reasoning || `Dashboard "${response.generated_dashboard?.title || 'Untitled'}" generated successfully.`,
            timestamp: new Date().toISOString(),
            dashboard_config: response.generated_dashboard,
            mode: mode
          };
          setMessages(prev => [...prev, assistantMessage]);
        }

        return response;
      } else {
        throw new Error('Dashboard generation failed');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to generate dashboard';
      setError(errorMessage);

      // Add error message to chat
      const errorChatMessage = {
        role: 'assistant',
        content: `Error: ${errorMessage}`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorChatMessage]);

      throw err;
    } finally {
      setLoading(false);
    }
  }, [messages, yamlConfig]);  // Add dependencies for conversation context

  /**
   * Apply generated configuration to Snowflake
   */
  const applyConfig = useCallback(async () => {
    if (!yamlConfig) {
      throw new Error('No configuration to apply');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await applyConfiguration({
        yaml_config: yamlConfig,
        dashboard_tab: generatedDashboard?.dashboard_tab
      });

      if (response.success) {
        // Add success message to chat
        const successMessage = {
          role: 'assistant',
          content: response.message,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, successMessage]);

        return response;
      } else {
        throw new Error('Failed to apply configuration');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to apply configuration';
      setError(errorMessage);

      // Add error message to chat
      const errorChatMessage = {
        role: 'assistant',
        content: `Error applying configuration: ${errorMessage}`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorChatMessage]);

      throw err;
    } finally {
      setLoading(false);
    }
  }, [yamlConfig, generatedDashboard]);

  /**
   * Clear chat history and generated dashboard
   */
  const clearChat = useCallback(() => {
    setMessages([]);
    setGeneratedDashboard(null);
    setYamlConfig(null);
    setValidationResult(null);
    setError(null);
  }, []);

  /**
   * Clear only the generated dashboard (keep chat history)
   */
  const clearGenerated = useCallback(() => {
    setGeneratedDashboard(null);
    setYamlConfig(null);
    setValidationResult(null);
  }, []);

  const value = {
    // Chat state
    messages,
    loading,
    error,

    // Generated dashboard state
    generatedDashboard,
    yamlConfig,
    validationResult,

    // Metrics state
    availableMetrics,
    metricsLoading,

    // Actions
    generate,
    applyConfig,
    fetchMetrics,
    clearChat,
    clearGenerated
  };

  return (
    <AgentContext.Provider value={value}>
      {children}
    </AgentContext.Provider>
  );
};

export default AgentContext;
